(() => {
  'use strict';

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const instances = new WeakMap();

  class DearOreHeroSlider {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-dearore-hero-track]');
      this.originalSlides = [...root.querySelectorAll('[data-dearore-hero-slide]')];
      this.dots = [...root.querySelectorAll('[data-dearore-hero-dot]')];
      this.index = this.originalSlides.length > 1 ? 1 : 0;
      this.timer = 0;
      this.locked = false;
      this.touchStartX = 0;
      if (!this.track || !this.originalSlides.length) return;
      this.setup();
    }

    setup() {
      if (this.originalSlides.length > 1) {
        const firstClone = this.originalSlides[0].cloneNode(true);
        const lastClone = this.originalSlides.at(-1).cloneNode(true);
        firstClone.dataset.dearoreHeroClone = 'true';
        lastClone.dataset.dearoreHeroClone = 'true';
        firstClone.removeAttribute('id');
        lastClone.removeAttribute('id');
        firstClone.removeAttribute('data-shopify-editor-block');
        lastClone.removeAttribute('data-shopify-editor-block');
        this.track.prepend(lastClone);
        this.track.append(firstClone);
      }
      this.slides = [...this.track.children];
      this.jump(false);
      this.updateA11y();
      this.bind();
      this.start();
    }

    bind() {
      this.root.querySelector('[data-dearore-hero-previous]')?.addEventListener('click', () => this.move(-1));
      this.root.querySelector('[data-dearore-hero-next]')?.addEventListener('click', () => this.move(1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.dearoreHeroDot))));
      this.track.addEventListener('transitionend', () => this.handleLoop());
      this.root.addEventListener('touchstart', (event) => { this.touchStartX = event.touches[0]?.clientX || 0; }, { passive: true });
      this.root.addEventListener('touchend', (event) => {
        const distance = (event.changedTouches[0]?.clientX || 0) - this.touchStartX;
        if (Math.abs(distance) > 45) this.move(distance > 0 ? -1 : 1);
      }, { passive: true });
      if (this.root.dataset.pauseHover === 'true') {
        this.root.addEventListener('pointerenter', () => this.stop());
        this.root.addEventListener('pointerleave', () => this.start());
      }
      this.root.addEventListener('focusin', () => this.stop());
      this.root.addEventListener('focusout', () => this.start());
    }

    realIndex() {
      if (this.originalSlides.length <= 1) return 0;
      return (this.index - 1 + this.originalSlides.length) % this.originalSlides.length;
    }

    jump(animate = true) {
      this.track.style.transitionDuration = animate ? '' : '0ms';
      this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;
      if (!animate) requestAnimationFrame(() => { this.track.style.transitionDuration = ''; });
      this.updateA11y();
    }

    move(direction) {
      if (this.originalSlides.length <= 1 || this.locked) return;
      this.locked = true;
      this.index += direction;
      this.jump(true);
      this.restart();
      window.setTimeout(() => { this.locked = false; }, 1300);
    }

    goTo(realIndex) {
      if (this.originalSlides.length <= 1) return;
      this.index = Math.max(0, Math.min(this.originalSlides.length - 1, realIndex)) + 1;
      this.jump(true);
      this.restart();
    }

    handleLoop() {
      this.locked = false;
      if (this.index === 0) {
        this.index = this.originalSlides.length;
        this.jump(false);
      } else if (this.index === this.slides.length - 1) {
        this.index = 1;
        this.jump(false);
      }
    }

    updateA11y() {
      const active = this.realIndex();
      this.dots.forEach((dot, index) => {
        if (index === active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.slides?.forEach((slide, index) => {
        const isActive = this.originalSlides.length <= 1 || index === this.index;
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.querySelectorAll('a, button, input, select, textarea').forEach((item) => {
          if (isActive) item.removeAttribute('tabindex');
          else item.setAttribute('tabindex', '-1');
        });
      });
    }

    start() {
      if (reduceMotion || this.root.dataset.autoplay !== 'true' || this.originalSlides.length <= 1 || document.hidden) return;
      this.stop();
      const interval = Math.max(1000, Number(this.root.dataset.interval) || 2500);
      this.timer = window.setInterval(() => this.move(1), interval);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = 0;
    }

    restart() {
      this.stop();
      this.start();
    }

    destroy() {
      this.stop();
      this.root.querySelectorAll('[data-dearore-hero-clone]').forEach((clone) => clone.remove());
    }
  }

  function mount(root = document) {
    const sliders = root.matches?.('[data-dearore-hero-slider]')
      ? [root]
      : [...root.querySelectorAll('[data-dearore-hero-slider]')];
    sliders.forEach((slider) => {
      instances.get(slider)?.destroy();
      instances.set(slider, new DearOreHeroSlider(slider));
    });
  }

  mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const slider = event.target.querySelector?.('[data-dearore-hero-slider]');
    instances.get(slider)?.destroy();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const selected = event.target.closest?.('[data-dearore-hero-slide]');
    const root = selected?.closest?.('[data-dearore-hero-slider]');
    const instance = instances.get(root);
    if (!instance || !selected) return;
    instance.goTo(instance.originalSlides.indexOf(selected));
    instance.stop();
  });
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('[data-dearore-hero-slider]').forEach((root) => {
      const instance = instances.get(root);
      if (document.hidden) instance?.stop();
      else instance?.start();
    });
  });
})();
